import { ChevronDown } from '@carbon/react/icons';
import { ReactNode, useState } from 'react';
import styles from './filter-section.module.scss';

interface FilterSectionProps {
  title: string;
  children: ReactNode;
  defaultExpanded?: boolean;
}

/**
 * Collapsible section for grouping related filters.
 * Renders a clickable header that toggles content visibility.
 * @param {FilterSectionProps} props - Component props
 * @param {string} props.title - Section title displayed in header
 * @param {ReactNode} props.children - Filter controls to render when expanded
 * @param {boolean} [props.defaultExpanded] - Initial expanded state (default: true)
 * @return {JSX.Element} The filter section element
 */
export function FilterSection({
  title,
  children,
  defaultExpanded = true,
}: FilterSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className={styles.filterSection}>
      <button
        type="button"
        className={styles.filterSectionHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.filterSectionTitle}>{title}</span>
        <ChevronDown
          className={`${styles.filterSectionChevron} ${
            isExpanded ? styles.filterSectionChevronOpen : ''
          }`}
        />
      </button>
      {isExpanded && (
        <div className={styles.filterSectionContent}>{children}</div>
      )}
    </div>
  );
}
